import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Typography,
  Box,
  Chip,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Collapse,
  useTheme,
  alpha,
  Button,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  Calculate as CalculateIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Visibility as VisibilityIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateRight as RotateRightIcon,
  RestartAlt as RestartAltIcon,
  PanTool as PanToolIcon,
} from '@mui/icons-material';
import { Sensor, SensorData, TipoSensor, FOSSensor, FOSTipologia } from '../types';
import { CorporateDialog } from './ui/CorporateDialog';
import { CorporateButton } from './ui/CorporateButton';
import { CorporateTextField } from './ui/CorporateTextField';
import { apiService } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useJobs } from '../contexts/JobsContext';

interface SensorMetadataProps {
  sensores: Sensor[];
  isLoading?: boolean;
  ensayoId: string;
  onSensoresUpdate?: (silent?: boolean) => void;
  datosPorSensor?: Record<string, SensorData[]>;
  onReloadSensorData?: (sensorId: string) => void;
  displacementStatus?: string | null;
  fosStatus?: string | null;
  ensayoCodigo?: string;
}

const tipoColors: Record<TipoSensor, string> = {
  celula_carga: '#1976d2',
  lvdt: '#388e3c',
  fibra_optica: '#d32f2f',
  computacion: '#f57c00',
  desconocido: '#9e9e9e',
};

const tipoLabels: Record<TipoSensor, string> = {
  celula_carga: 'Célula de Carga',
  lvdt: 'LVDT',
  fibra_optica: 'Fibra Óptica',
  computacion: 'Computación',
  desconocido: 'Desconocido',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sensor-tabpanel-${index}`}
      aria-labelledby={`sensor-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export const SensorMetadata: React.FC<SensorMetadataProps> = ({
  sensores,
  isLoading = false,
  ensayoId,
  onSensoresUpdate,
  datosPorSensor = {},
  onReloadSensorData,
  displacementStatus,
  fosStatus,
  ensayoCodigo,
}) => {
  const theme = useTheme();
  const { showSuccess, showError } = useToast();
  const [selectedTab, setSelectedTab] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  
  // Estados para diálogo de longitud de medición
  const [longitudDialogOpen, setLongitudDialogOpen] = useState(false);
  const [sensorSeleccionado, setSensorSeleccionado] = useState<Sensor | null>(null);
  const [longitudValue, setLongitudValue] = useState<string>('');
  const [isSavingLongitud, setIsSavingLongitud] = useState(false);
  
  // Estados para calcular desplazamientos
  const [isCalculating, setIsCalculating] = useState(false);
  const [desplazamientosStatus, setDesplazamientosStatus] = useState<{
    calculados: boolean;
    mensaje: string;
  } | null>(null);

  const { addJob, activeJobs } = useJobs();
  
  // Check if this specific job is running in global context to sync local state
  const displacementJobId = `calc-displacement-${ensayoId}`;
  const isDisplacementJobActive = activeJobs.some(j => j.id === displacementJobId);
  
  // Sincronizar estado local con el estado del backend y el job
  useEffect(() => {
    // DEBUG: Log para identificar el problema de status
    console.log('[SensorMetadata] displacementStatus received:', displacementStatus, '| isDisplacementJobActive:', isDisplacementJobActive);
    
    // Si el estado del backend indica que está procesando, mostrar spinner
    if (displacementStatus === 'PROCESSING' || displacementStatus === 'PENDING') {
        setIsCalculating(true);
    } 
    // Si el job está activo, también mostrar spinner
    else if (isDisplacementJobActive) {
        setIsCalculating(true);
    }
    // Si el estado es final y el job no está activo, ocultar spinner y actualizar mensaje
    else if ((displacementStatus === 'COMPLETED' || displacementStatus === 'ERROR') && !isDisplacementJobActive) {
        setIsCalculating(false);
        if (displacementStatus === 'COMPLETED') {
             setDesplazamientosStatus({ calculados: true, mensaje: 'Cálculo de desplazamientos completado exitosamente.' });
        } else if (displacementStatus === 'ERROR') {
             setDesplazamientosStatus({ calculados: false, mensaje: 'Error en el cálculo de desplazamientos.' });
        }
    }
    // Si no hay estado (NONE o null) y no hay job activo, mantener el estado actual
    // (no resetear isCalculating si el usuario acaba de hacer clic)
  }, [displacementStatus, isDisplacementJobActive]);

  // Polling para verificar el estado del cálculo de desplazamientos via JobsContext
  useEffect(() => {
    // Solo iniciar polling si el estado está en proceso o si estamos calculando
    // Si el estado es ERROR o COMPLETED, no necesitamos polling
    if (displacementStatus === 'PROCESSING' || displacementStatus === 'PENDING' || isCalculating) {
         addJob({
            id: displacementJobId,
            title: ensayoCodigo ? `[${ensayoCodigo}] Cálculo de Desplazamientos` : 'Cálculo de Desplazamientos',
            pollingFn: async () => {
                try {
                    const ensayo = await apiService.getEnsayo(ensayoId);
                    const status = ensayo.displacement_calculation_status;
                    // Map API status to JobStatus
                    if (status === 'COMPLETED') return 'COMPLETED';
                    if (status === 'ERROR') return 'ERROR';
                    // Si el estado cambió a NONE o algo inesperado, considerarlo como finalizado
                    if (status === 'NONE' || !status) return 'COMPLETED';
                    return 'PROCESSING';
                } catch (error: any) {
                    console.error('Error en polling de desplazamientos:', error);
                    // Si hay error de autenticación o similar, no actualizar el estado del job
                    // para evitar falsos positivos. El job se mantendrá en PROCESSING hasta
                    // que se resuelva el problema de autenticación o el estado cambie
                    return 'PROCESSING';
                }
            },
            onComplete: () => {
                setIsCalculating(false);
                if (onSensoresUpdate) onSensoresUpdate(true);
                if (onReloadSensorData) {
                    const sensoresLVDT = sensores.filter(s => {
                        const tipo = s.tipo_manual || s.tipo;
                        return tipo === 'lvdt' && !s.nombre.toUpperCase().includes('_REF');
                    });
                    sensoresLVDT.forEach(sensor => onReloadSensorData(sensor.id));
                }
                showSuccess('Cálculo de desplazamientos completado exitosamente.');
                setDesplazamientosStatus({ calculados: true, mensaje: 'Cálculo de desplazamientos completado exitosamente.' });
            },
            onError: (err) => {
                setIsCalculating(false);
                showError('Error en el cálculo de desplazamientos.');
                setDesplazamientosStatus({ calculados: false, mensaje: 'Error en el cálculo de desplazamientos.' });
            }
         });
    } else if (displacementStatus === 'ERROR' || displacementStatus === 'COMPLETED') {
        // Si el estado ya es final, asegurar que el estado local esté sincronizado
        setIsCalculating(false);
    }
  }, [displacementStatus, isCalculating, ensayoId, addJob, showSuccess, showError, onSensoresUpdate, onReloadSensorData, sensores]);

  // Estados para calcular strains FOS
  const [isCalculatingStrains, setIsCalculatingStrains] = useState(false);
  const [strainsStatus, setStrainsStatus] = useState<{
    calculados: boolean;
    mensaje: string;
  } | null>(null);

  const fosJobId = `calc-fos-${ensayoId}`;
  const isFosJobActive = activeJobs.some(j => j.id === fosJobId);
  
  // Sync local loading state and status
  useEffect(() => {
    if (fosStatus === 'PROCESSING' || fosStatus === 'PENDING') {
        setIsCalculatingStrains(true);
    } else if (isFosJobActive) {
        setIsCalculatingStrains(true);
    } else if ((fosStatus === 'COMPLETED' || fosStatus === 'ERROR') && !isFosJobActive) {
        setIsCalculatingStrains(false);
        if (fosStatus === 'COMPLETED') {
            setStrainsStatus({ calculados: true, mensaje: 'Cálculo de strains FOS completado exitosamente.' });
        } else if (fosStatus === 'ERROR') {
            setStrainsStatus({ calculados: false, mensaje: 'Error en el cálculo de strains FOS.' });
        }
    } else if (!isFosJobActive && isCalculatingStrains && !fosStatus) {
        // Fallback: si no hay job activo y no hay status del backend pero estamos calculando localmente
        // podría ser que el job terminó pero no se actualizó el status o se canceló
        setIsCalculatingStrains(false);
    }
  }, [fosStatus, isFosJobActive, isCalculatingStrains]);

  // Polling para verificar el estado del cálculo de strains FOS via JobsContext
  useEffect(() => {
    // Solo iniciar polling si el estado está en proceso o si estamos calculando
    // Si el estado es ERROR o COMPLETED, no necesitamos polling
    if (fosStatus === 'PROCESSING' || fosStatus === 'PENDING' || isCalculatingStrains) {
        addJob({
            id: fosJobId,
            title: ensayoCodigo ? `[${ensayoCodigo}] Cálculo de Strains FOS` : 'Cálculo de Strains FOS',
            pollingFn: async () => {
                try {
                    const ensayo = await apiService.getEnsayo(ensayoId);
                    const status = ensayo.fos_strain_calculation_status;
                    if (status === 'COMPLETED') return 'COMPLETED';
                    if (status === 'ERROR') return 'ERROR';
                    // Si el estado cambió a NONE o algo inesperado, considerarlo como finalizado
                    if (status === 'NONE' || !status) return 'COMPLETED';
                    return 'PROCESSING';
                } catch (error: any) {
                    console.error('Error en polling de FOS strains:', error);
                    // Si hay error en el polling, no actualizar el estado del job
                    // para evitar falsos positivos
                    return 'PROCESSING';
                }
            },
            onComplete: () => {
                setIsCalculatingStrains(false);
                if (onSensoresUpdate) onSensoresUpdate(true);
                if (onReloadSensorData) {
                    const sensoresFOS = sensores.filter(s => {
                        const tipo = s.tipo_manual || s.tipo;
                        return tipo === 'fibra_optica';
                    });
                    sensoresFOS.forEach(sensor => onReloadSensorData(sensor.id));
                }
                showSuccess('Cálculo de strains FOS completado exitosamente.');
                setStrainsStatus({ calculados: true, mensaje: 'Cálculo de strains FOS completado exitosamente.' });
            },
            onError: (err) => {
                setIsCalculatingStrains(false);
                showError('Error en el cálculo de strains FOS.');
                setStrainsStatus({ calculados: false, mensaje: 'Error en el cálculo de strains FOS.' });
            }
        });
    } else if (fosStatus === 'ERROR' || fosStatus === 'COMPLETED') {
        // Si el estado ya es final, asegurar que el estado local esté sincronizado
        setIsCalculatingStrains(false);
    }
  }, [fosStatus, isCalculatingStrains, ensayoId, addJob, showSuccess, showError, onSensoresUpdate, onReloadSensorData, sensores]);
  const [simplifiedMode, setSimplifiedMode] = useState(false);
  const [showStrainsInfo, setShowStrainsInfo] = useState(false);
  
  // Estados para diálogo de l_FAL y coordenadas (FOS)
  const [lFalDialogOpen, setLFalDialogOpen] = useState(false);
  const [sensorFALSeleccionado, setSensorFALSeleccionado] = useState<Sensor | null>(null);
  const [lFalValue, setLFalValue] = useState<string>('');
  const [positionX, setPositionX] = useState<string>('');
  const [positionY, setPositionY] = useState<string>('');
  const [positionZ, setPositionZ] = useState<string>('');
  const [isSavingLFal, setIsSavingLFal] = useState(false);
  
  // Estados para diálogo de CTE del ensayo y dimensiones de la viga
  const [cteDialogOpen, setCteDialogOpen] = useState(false);
  const [cteValue, setCteValue] = useState<string>('');
  const [beamLengthValue, setBeamLengthValue] = useState<string>('');
  const [beamWidthValue, setBeamWidthValue] = useState<string>('');
  const [beamHeightValue, setBeamHeightValue] = useState<string>('');
  const [isSavingCte, setIsSavingCte] = useState(false);
  const [ensayoCte, setEnsayoCte] = useState<number | null>(null);
  const [ensayoBeamDimensions, setEnsayoBeamDimensions] = useState<{
    length: number | null;
    width: number | null;
    height: number | null;
  }>({ length: null, width: null, height: null });
  
  // Estados para visualización 3D
  const [view3DDialogOpen, setView3DDialogOpen] = useState(false);
  const [sensor3DSeleccionado, setSensor3DSeleccionado] = useState<Sensor | null>(null);
  const canvas3DRef = useRef<HTMLCanvasElement | null>(null);
  const [zoom3D, setZoom3D] = useState(1);
  const [pan3D, setPan3D] = useState({ x: 0, y: 0 });
  const [isDragging3D, setIsDragging3D] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [rotation3D, setRotation3D] = useState({ x: Math.PI / 6, y: Math.PI / 6 });
  const beamDimensionsRef = useRef<{ length: number; width: number; height: number } | null>(null);
  
  // Función para dibujar el canvas 3D con zoom, pan y rotación
  const drawCanvas3D = useCallback(async () => {
    if (!canvas3DRef.current || !sensor3DSeleccionado) return;
    
    const canvas = canvas3DRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    try {
      // Cargar dimensiones del ensayo si no están en el ref
      if (!beamDimensionsRef.current) {
        const ensayo = await apiService.getEnsayo(ensayoId);
        beamDimensionsRef.current = {
          length: ensayo.beam_length || 2000,
          width: ensayo.beam_width || 200,
          height: ensayo.beam_height || 300,
        };
      }
      
      const { length: beamLength, width: beamWidth, height: beamHeight } = beamDimensionsRef.current;
      
      // Esperar a que el canvas tenga dimensiones válidas
      const waitForCanvas = () => {
        return new Promise<void>((resolve) => {
          let attempts = 0;
          const maxAttempts = 20;
          const checkCanvas = () => {
            const rect = canvas.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              resolve();
            } else if (attempts < maxAttempts) {
              attempts++;
              setTimeout(checkCanvas, 50);
            } else {
              resolve();
            }
          };
          checkCanvas();
        });
      };
      
      await waitForCanvas();
      
      // Configurar tamaño del canvas
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      
      // Limpiar canvas con fondo degradado
      const gradient = ctx.createLinearGradient(0, 0, 0, rect.height);
      gradient.addColorStop(0, theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5');
      gradient.addColorStop(1, theme.palette.mode === 'dark' ? '#0a0a0a' : '#e0e0e0');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, rect.width, rect.height);
      
      // Calcular escala automática para maximizar la viga
      const padding = 80;
      const availableWidth = rect.width - padding * 2;
      const availableHeight = rect.height - padding * 2;
      
      // Calcular el bounding box de la viga en 3D
      const maxDim = Math.max(beamLength, beamWidth, beamHeight);
      const scaleX = availableWidth / maxDim;
      const scaleY = availableHeight / maxDim;
      const baseScale = Math.min(scaleX, scaleY) * 0.9;
      
      // Aplicar zoom
      const scale = baseScale * zoom3D;
      
      // Centro con pan aplicado
      const centerX = rect.width / 2 + pan3D.x;
      const centerY = rect.height / 2 + pan3D.y;
      
      // Rotación 3D (eje X e Y)
      const cosX = Math.cos(rotation3D.x);
      const sinX = Math.sin(rotation3D.x);
      const cosY = Math.cos(rotation3D.y);
      const sinY = Math.sin(rotation3D.y);
      
      // Función de proyección 3D con rotación
      const project3D = (x: number, y: number, z: number) => {
        // Rotar alrededor del eje Y
        let x1 = x * cosY - z * sinY;
        let z1 = x * sinY + z * cosY;
        // Rotar alrededor del eje X
        let y1 = y * cosX - z1 * sinX;
        z1 = y * sinX + z1 * cosX;
        
        // Proyección isométrica
        const isoX = (x1 - y1) * 0.707;
        const isoY = (x1 + y1) * 0.707 - z1;
        
        return {
          x: centerX + isoX * scale,
          y: centerY + isoY * scale,
          z: z1
        };
      };
      
      // Calcular puntos de la viga
      const beamPoints = [
        project3D(0, 0, 0),
        project3D(beamLength, 0, 0),
        project3D(beamLength, beamWidth, 0),
        project3D(0, beamWidth, 0),
        project3D(0, 0, beamHeight),
        project3D(beamLength, 0, beamHeight),
        project3D(beamLength, beamWidth, beamHeight),
        project3D(0, beamWidth, beamHeight),
      ];
      
      // Ordenar caras por profundidad (z-buffer simple)
      const faces = [
        { points: [beamPoints[0], beamPoints[1], beamPoints[2], beamPoints[3]], z: (beamPoints[0].z + beamPoints[1].z + beamPoints[2].z + beamPoints[3].z) / 4, color: theme.palette.mode === 'dark' ? '#4a5568' : '#cbd5e0' }, // Frente
        { points: [beamPoints[4], beamPoints[5], beamPoints[6], beamPoints[7]], z: (beamPoints[4].z + beamPoints[5].z + beamPoints[6].z + beamPoints[7].z) / 4, color: theme.palette.mode === 'dark' ? '#2d3748' : '#a0aec0' }, // Atrás
        { points: [beamPoints[0], beamPoints[1], beamPoints[5], beamPoints[4]], z: (beamPoints[0].z + beamPoints[1].z + beamPoints[5].z + beamPoints[4].z) / 4, color: theme.palette.mode === 'dark' ? '#3d4856' : '#b8c5d1' }, // Abajo
        { points: [beamPoints[2], beamPoints[3], beamPoints[7], beamPoints[6]], z: (beamPoints[2].z + beamPoints[3].z + beamPoints[7].z + beamPoints[6].z) / 4, color: theme.palette.mode === 'dark' ? '#3d4856' : '#b8c5d1' }, // Arriba
        { points: [beamPoints[0], beamPoints[3], beamPoints[7], beamPoints[4]], z: (beamPoints[0].z + beamPoints[3].z + beamPoints[7].z + beamPoints[4].z) / 4, color: theme.palette.mode === 'dark' ? '#3d4856' : '#b8c5d1' }, // Izquierda
        { points: [beamPoints[1], beamPoints[2], beamPoints[6], beamPoints[5]], z: (beamPoints[1].z + beamPoints[2].z + beamPoints[6].z + beamPoints[5].z) / 4, color: theme.palette.mode === 'dark' ? '#3d4856' : '#b8c5d1' }, // Derecha
      ];
      
      // Ordenar por profundidad (pintar las más lejanas primero)
      faces.sort((a, b) => a.z - b.z);
      
      // Dibujar caras con sombras
      faces.forEach((face, idx) => {
        ctx.fillStyle = face.color;
        ctx.strokeStyle = theme.palette.mode === 'dark' ? '#1a202c' : '#718096';
        ctx.lineWidth = 2;
        
        ctx.beginPath();
        ctx.moveTo(face.points[0].x, face.points[0].y);
        for (let i = 1; i < face.points.length; i++) {
          ctx.lineTo(face.points[i].x, face.points[i].y);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      });
      
      // Dibujar aristas destacadas
      ctx.strokeStyle = theme.palette.mode === 'dark' ? '#4a5568' : '#718096';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      // Aristas principales
      ctx.moveTo(beamPoints[0].x, beamPoints[0].y);
      ctx.lineTo(beamPoints[1].x, beamPoints[1].y);
      ctx.lineTo(beamPoints[2].x, beamPoints[2].y);
      ctx.lineTo(beamPoints[3].x, beamPoints[3].y);
      ctx.closePath();
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(beamPoints[4].x, beamPoints[4].y);
      ctx.lineTo(beamPoints[5].x, beamPoints[5].y);
      ctx.lineTo(beamPoints[6].x, beamPoints[6].y);
      ctx.lineTo(beamPoints[7].x, beamPoints[7].y);
      ctx.closePath();
      ctx.stroke();
      
      // Aristas verticales
      ctx.beginPath();
      ctx.moveTo(beamPoints[0].x, beamPoints[0].y);
      ctx.lineTo(beamPoints[4].x, beamPoints[4].y);
      ctx.moveTo(beamPoints[1].x, beamPoints[1].y);
      ctx.lineTo(beamPoints[5].x, beamPoints[5].y);
      ctx.moveTo(beamPoints[2].x, beamPoints[2].y);
      ctx.lineTo(beamPoints[6].x, beamPoints[6].y);
      ctx.moveTo(beamPoints[3].x, beamPoints[3].y);
      ctx.lineTo(beamPoints[7].x, beamPoints[7].y);
      ctx.stroke();
      
      // Dibujar sensor si tiene coordenadas
      if (sensor3DSeleccionado.position_x !== null || 
          sensor3DSeleccionado.position_y !== null || 
          sensor3DSeleccionado.position_z !== null) {
        const sensorX = sensor3DSeleccionado.position_x ?? 0;
        const sensorY = sensor3DSeleccionado.position_y ?? beamWidth / 2;
        const sensorZ = sensor3DSeleccionado.position_z ?? beamHeight / 2;
        
        const sensorPos = project3D(sensorX, sensorY, sensorZ);
        
        // Sombra del punto del sensor
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.beginPath();
        ctx.arc(sensorPos.x + 2, sensorPos.y + 2, 10, 0, 2 * Math.PI);
        ctx.fill();
        
        // Punto del sensor con gradiente
        const sensorGradient = ctx.createRadialGradient(sensorPos.x, sensorPos.y, 0, sensorPos.x, sensorPos.y, 12);
        sensorGradient.addColorStop(0, theme.palette.primary.main);
        sensorGradient.addColorStop(0.7, theme.palette.primary.dark);
        sensorGradient.addColorStop(1, 'transparent');
        ctx.fillStyle = sensorGradient;
        ctx.beginPath();
        ctx.arc(sensorPos.x, sensorPos.y, 12, 0, 2 * Math.PI);
        ctx.fill();
        
        // Borde del punto
        ctx.strokeStyle = theme.palette.primary.dark;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(sensorPos.x, sensorPos.y, 12, 0, 2 * Math.PI);
        ctx.stroke();
        
        // Línea desde el centro de la viga al sensor
        const centerPos = project3D(beamLength / 2, beamWidth / 2, beamHeight / 2);
        ctx.strokeStyle = alpha(theme.palette.primary.main, 0.6);
        ctx.lineWidth = 2;
        ctx.setLineDash([8, 4]);
        ctx.beginPath();
        ctx.moveTo(centerPos.x, centerPos.y);
        ctx.lineTo(sensorPos.x, sensorPos.y);
        ctx.stroke();
        ctx.setLineDash([]);
        
        // Etiqueta del sensor con fondo
        const labelText = 'Sensor';
        ctx.font = 'bold 13px sans-serif';
        const textMetrics = ctx.measureText(labelText);
        const labelWidth = textMetrics.width + 12;
        const labelHeight = 20;
        
        // Fondo de la etiqueta
        ctx.fillStyle = alpha(theme.palette.background.paper, 0.95);
        ctx.strokeStyle = theme.palette.primary.main;
        ctx.lineWidth = 1.5;
        const labelX = sensorPos.x + 15;
        const labelY = sensorPos.y - labelHeight / 2;
        const radius = 4;
        ctx.beginPath();
        ctx.moveTo(labelX + radius, labelY);
        ctx.lineTo(labelX + labelWidth - radius, labelY);
        ctx.quadraticCurveTo(labelX + labelWidth, labelY, labelX + labelWidth, labelY + radius);
        ctx.lineTo(labelX + labelWidth, labelY + labelHeight - radius);
        ctx.quadraticCurveTo(labelX + labelWidth, labelY + labelHeight, labelX + labelWidth - radius, labelY + labelHeight);
        ctx.lineTo(labelX + radius, labelY + labelHeight);
        ctx.quadraticCurveTo(labelX, labelY + labelHeight, labelX, labelY + labelHeight - radius);
        ctx.lineTo(labelX, labelY + radius);
        ctx.quadraticCurveTo(labelX, labelY, labelX + radius, labelY);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        
        // Texto de la etiqueta
        ctx.fillStyle = theme.palette.primary.main;
        ctx.fillText(labelText, sensorPos.x + 21, sensorPos.y + 5);
      }
      
      // Dibujar ejes de coordenadas
      const axisLength = Math.min(beamLength, beamWidth, beamHeight) * 0.3;
      const origin = project3D(0, 0, 0);
      
      // Eje X (rojo)
      const xEnd = project3D(axisLength, 0, 0);
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(origin.x, origin.y);
      ctx.lineTo(xEnd.x, xEnd.y);
      ctx.stroke();
      ctx.fillStyle = '#ef4444';
      ctx.font = 'bold 11px sans-serif';
      ctx.fillText('X', xEnd.x + 5, xEnd.y);
      
      // Eje Y (verde)
      const yEnd = project3D(0, axisLength, 0);
      ctx.strokeStyle = '#10b981';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(origin.x, origin.y);
      ctx.lineTo(yEnd.x, yEnd.y);
      ctx.stroke();
      ctx.fillStyle = '#10b981';
      ctx.fillText('Y', yEnd.x + 5, yEnd.y);
      
      // Eje Z (azul)
      const zEnd = project3D(0, 0, axisLength);
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(origin.x, origin.y);
      ctx.lineTo(zEnd.x, zEnd.y);
      ctx.stroke();
      ctx.fillStyle = '#3b82f6';
      ctx.fillText('Z', zEnd.x + 5, zEnd.y);
      
    } catch (error) {
      console.error('Error al dibujar canvas 3D:', error);
    }
  }, [canvas3DRef, sensor3DSeleccionado, theme, ensayoId, zoom3D, pan3D, rotation3D]);
  
  // Handlers para controles 3D
  const handleZoom3D = useCallback((delta: number) => {
    setZoom3D(prev => Math.max(0.1, Math.min(5, prev + delta)));
  }, []);
  
  const handleReset3D = useCallback(() => {
    setZoom3D(1);
    setPan3D({ x: 0, y: 0 });
    setRotation3D({ x: Math.PI / 6, y: Math.PI / 6 });
  }, []);
  
  const handleMouseDown3D = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging3D(true);
    setDragStart({ x: e.clientX - pan3D.x, y: e.clientY - pan3D.y });
  }, [pan3D]);
  
  const handleMouseMove3D = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isDragging3D) {
      setPan3D({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  }, [isDragging3D, dragStart]);
  
  const handleMouseUp3D = useCallback(() => {
    setIsDragging3D(false);
  }, []);
  
  const handleWheel3D = useCallback((e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    handleZoom3D(delta);
  }, [handleZoom3D]);
  
  // Efecto para dibujar el canvas 3D cuando cambian los parámetros
  useEffect(() => {
    if (view3DDialogOpen && sensor3DSeleccionado) {
      const timer = setTimeout(() => {
        drawCanvas3D();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [view3DDialogOpen, sensor3DSeleccionado, zoom3D, pan3D, rotation3D, drawCanvas3D]);
  
  // Efecto para cargar dimensiones cuando se abre el diálogo
  useEffect(() => {
    if (view3DDialogOpen && sensor3DSeleccionado) {
      const loadDimensions = async () => {
        try {
          const ensayo = await apiService.getEnsayo(ensayoId);
          beamDimensionsRef.current = {
            length: ensayo.beam_length || 2000,
            width: ensayo.beam_width || 200,
            height: ensayo.beam_height || 300,
          };
          // Resetear controles cuando se cargan nuevas dimensiones
          setZoom3D(1);
          setPan3D({ x: 0, y: 0 });
          setRotation3D({ x: Math.PI / 6, y: Math.PI / 6 });
        } catch (error) {
          console.error('Error al cargar dimensiones:', error);
        }
      };
      loadDimensions();
    }
  }, [view3DDialogOpen, sensor3DSeleccionado, ensayoId]);
  
  // Redibujar cuando se redimensiona la ventana
  useEffect(() => {
    const handleResize = () => {
      if (view3DDialogOpen && sensor3DSeleccionado) {
        drawCanvas3D();
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [view3DDialogOpen, sensor3DSeleccionado, drawCanvas3D]);
  
  // Estados para diálogo de asociación FOS
  const [fosDialogOpen, setFosDialogOpen] = useState(false);
  const [sensorFOSSeleccionado, setSensorFOSSeleccionado] = useState<Sensor | null>(null);
  const [fosSensors, setFosSensors] = useState<FOSSensor[]>([]);
  const [isLoadingFOS, setIsLoadingFOS] = useState(false);
  const [fosSearchTerm, setFosSearchTerm] = useState('');
  const [fosTipologiaFilter, setFosTipologiaFilter] = useState<string>('all'); // 'all', 'Long-Gauge', 'Short-Gauge'
  const [fosGLFilter, setFosGLFilter] = useState('');

  // Determinar tipología basada en product_type (misma lógica que FOSSensorsPage)
  // SC-01/T: Long-Gauge, MS-03/SGCalsens: Short-Gauge
  const getTipologiaFromProductType = (productType: string): FOSTipologia => {
    if (!productType) return 'Long-Gauge';
    const pt = productType.toUpperCase();
    if (pt.includes('SC-01/T')) {
      return 'Long-Gauge';
    } else if (pt.includes('MS-03') || pt.includes('SGCALSENS')) {
      return 'Short-Gauge';
    }
    return 'Long-Gauge'; // Por defecto
  };

  // Agrupar sensores por tipo
  const sensoresPorTipo = useMemo(() => {
    return sensores.reduce((acc, sensor) => {
      const tipo = sensor.tipo_manual || sensor.tipo;
      if (!acc[tipo]) {
        acc[tipo] = [];
      }
      acc[tipo].push(sensor);
      return acc;
    }, {} as Record<TipoSensor, Sensor[]>);
  }, [sensores]);
  
  // Detectar sensor de referencia LVDT
  const sensorReferenciaLVDT = useMemo(() => {
    return sensores.find(s => {
      const tipo = s.tipo_manual || s.tipo;
      return tipo === 'lvdt' && s.nombre.toUpperCase().includes('_REF');
    });
  }, [sensores]);

  // Obtener valores únicos de Gauge Length para el filtro
  const uniqueGaugeLengths = useMemo(() => {
    const gls = new Set<string>();
    fosSensors.forEach(sensor => {
      if (sensor.gauge_length) {
        gls.add(sensor.gauge_length);
      }
    });
    // Ordenar numéricamente si parece un número, o alfabéticamente
    return Array.from(gls).sort((a, b) => {
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
        }
        return a.localeCompare(b);
    });
  }, [fosSensors]);
  
  // Verificar si hay desplazamientos calculados
  const verificarDesplazamientos = useMemo(() => {
    const sensoresLVDT = sensores.filter(s => {
      const tipo = s.tipo_manual || s.tipo;
      return tipo === 'lvdt' && !s.nombre.toUpperCase().includes('_REF');
    });
    
    // Por ahora, asumimos que si todos los sensores tienen longitud_medicion y sensor_referencia_id,
    // los desplazamientos podrían estar calculados
    // En el futuro, podríamos verificar si hay datos con desplazamiento no nulo
    const todosConfigurados = sensoresLVDT.every(s => 
      s.longitud_medicion !== null && s.sensor_referencia_id !== null
    );
    
    return {
      calculados: todosConfigurados && sensoresLVDT.length > 0,
      mensaje: todosConfigurados 
        ? 'Los desplazamientos están configurados. Usa el botón "Calcular desplazamientos" para calcularlos.'
        : 'Configura la longitud de medición y el sensor de referencia para todos los sensores LVDT.',
    };
  }, [sensores]);
  
  // Abrir diálogo para introducir longitud de medición
  const handleOpenLongitudDialog = (sensor: Sensor) => {
    setSensorSeleccionado(sensor);
    setLongitudValue(sensor.longitud_medicion?.toString() || '');
    setLongitudDialogOpen(true);
  };
  
  // Guardar longitud de medición
  const handleSaveLongitud = async () => {
    if (!sensorSeleccionado) return;
    
    const longitud = parseFloat(longitudValue);
    if (isNaN(longitud) || longitud <= 0) {
      showError('La longitud de medición debe ser un número positivo');
      return;
    }
    
    setIsSavingLongitud(true);
    try {
      // Asignar sensor de referencia si no está asignado
      const updateData: Partial<Sensor> = {
        longitud_medicion: longitud,
      };
      
      if (!sensorSeleccionado.sensor_referencia_id && sensorReferenciaLVDT) {
        updateData.sensor_referencia_id = sensorReferenciaLVDT.id;
      }
      
      await apiService.updateSensor(ensayoId, sensorSeleccionado.id, updateData);
      showSuccess(`Longitud de medición guardada: ${longitud} mm`);
      setLongitudDialogOpen(false);
      setSensorSeleccionado(null);
      setLongitudValue('');
      if (onSensoresUpdate) {
        onSensoresUpdate();
      }
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Error al guardar la longitud de medición');
    } finally {
      setIsSavingLongitud(false);
    }
  };
  
  // Abrir diálogo para asociar sensor FOS
  const handleOpenFOSDialog = async (sensor: Sensor) => {
    setSensorFOSSeleccionado(sensor);
    setIsLoadingFOS(true);
    try {
      const result = await apiService.getFOSSensors({ limit: 100, is_active: true });
      setFosSensors(result.sensors);
      setFosDialogOpen(true);
    } catch (error: any) {
      showError('Error al cargar sensores FOS de la base de datos');
    } finally {
      setIsLoadingFOS(false);
    }
  };

  // Cerrar diálogo FOS
  const handleCloseFOSDialog = () => {
    setFosDialogOpen(false);
    setSensorFOSSeleccionado(null);
    setFosSearchTerm('');
    setFosTipologiaFilter('all');
    setFosGLFilter('');
  };

  // Helper para parsear Gauge Length a metros
  const parseGaugeLength = (gl: string): number | null => {
    if (!gl) return null;
    // Reemplazar comas por puntos y limpiar espacios
    const cleanGl = gl.replace(',', '.').trim().toLowerCase();
    
    // Extraer valor numérico y unidad
    // Soporta: "0.5m", "0,5m", "500mm", "50 cm", "1" (asume m)
    const match = cleanGl.match(/^([0-9.]+)\s*(mm|cm|m)?$/);
    if (!match) return null;
    
    const value = parseFloat(match[1]);
    const unit = match[2] || 'm'; // Asumir metros si no hay unidad
    
    if (isNaN(value)) return null;
    
    switch (unit) {
      case 'mm': return value / 1000;
      case 'cm': return value / 100;
      case 'm': return value;
      default: return value;
    }
  };

  // Asociar sensor FOS
  const handleAsociarFOS = async (fosSensorId: string) => {
    if (!sensorFOSSeleccionado) return;
    
    try {
      // 1. Asociar el sensor FOS
      await apiService.asociarSensorFOS(ensayoId, sensorFOSSeleccionado.id, fosSensorId);
      
      // 2. Intentar actualizar l_fal con el Gauge Length del sensor seleccionado
      let glUpdated = false;
      let glValue: number | null = null;
      
      const selectedFOS = fosSensors.find(f => f.id === fosSensorId);
      if (selectedFOS && selectedFOS.gauge_length) {
        glValue = parseGaugeLength(selectedFOS.gauge_length);
        
        if (glValue !== null && glValue > 0) {
          try {
            await apiService.updateSensor(ensayoId, sensorFOSSeleccionado.id, {
              l_fal: glValue
            });
            glUpdated = true;
          } catch (updateError) {
            console.error('Error al actualizar l_fal automáticamente:', updateError);
            // No fallamos toda la operación si solo falla la actualización automática de l_fal
          }
        }
      }

      if (glUpdated && glValue !== null) {
        showSuccess(`Sensor FOS asociado y Longitud de Anclaje actualizada a ${glValue}m`);
      } else {
        showSuccess('Sensor FOS asociado correctamente');
      }
      
      handleCloseFOSDialog();
      if (onSensoresUpdate) {
        onSensoresUpdate();
      }
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Error al asociar sensor FOS');
    }
  };

  // Desasociar sensor FOS
  const handleDesasociarFOS = async (sensor: Sensor) => {
    try {
      await apiService.desasociarSensorFOS(ensayoId, sensor.id);
      showSuccess('Sensor FOS desasociado correctamente');
      if (onSensoresUpdate) {
        onSensoresUpdate();
      }
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Error al desasociar sensor FOS');
    }
  };

  // Calcular desplazamientos LVDT
  const handleCalcularDesplazamientos = async () => {
    // Establecer estado de cálculo inmediatamente para mostrar spinner
    setIsCalculating(true);
    try {
      const result = await apiService.calcularDesplazamientosLVDT(ensayoId);
      showSuccess(
        `Cálculo de desplazamientos iniciado. Este proceso se realiza en segundo plano y se le notificará al finalizar.`
      );
      setDesplazamientosStatus({
        calculados: true,
        mensaje: `Cálculo iniciado. Espere a que finalice el proceso...`,
      });
      // Forzar actualización del ensayo para obtener el estado actualizado (PENDING/PROCESSING)
      // Esto asegura que el useEffect detecte el cambio de estado y mantenga el spinner
      if (onSensoresUpdate) {
        // Usar setTimeout para dar tiempo al backend de actualizar el estado
        setTimeout(() => {
          onSensoresUpdate(false);
        }, 500);
      }
    } catch (error: any) {
      setIsCalculating(false);
      showError(error.response?.data?.detail || 'Error al iniciar el cálculo de desplazamientos');
      setDesplazamientosStatus({
        calculados: false,
        mensaje: error.response?.data?.detail || 'Error al iniciar el cálculo',
      });
    }
  };

  // Resetear estado de desplazamientos (manual recovery)
  const handleResetDesplazamientosStatus = async () => {
    try {
      await apiService.updateEnsayo(ensayoId, {
        displacement_calculation_status: 'ERROR'
      });
      showSuccess('Estado de cálculo reiniciado. Puede intentar calcular de nuevo.');
      if (onSensoresUpdate) onSensoresUpdate();
    } catch (error: any) {
      showError('Error al reiniciar el estado');
    }
  };
  
  // Calcular strains FOS
  const handleCalcularStrainsFOS = async () => {
    // Establecer estado de cálculo inmediatamente para mostrar spinner
    setIsCalculatingStrains(true);
    try {
      const result = await apiService.calcularStrainsFOS(ensayoId, simplifiedMode);
      showSuccess(
        `Cálculo de strains FOS iniciado${simplifiedMode ? ' (simplificado)' : ''}. Este proceso se realiza en segundo plano y se le notificará al finalizar.`
      );
      setStrainsStatus({
        calculados: true,
        mensaje: `Cálculo iniciado. Espere a que finalice el proceso...`,
      });
      // Forzar actualización del ensayo para obtener el estado actualizado (PENDING/PROCESSING)
      // Esto asegura que el useEffect detecte el cambio de estado y mantenga el spinner
      if (onSensoresUpdate) {
        // Usar setTimeout para dar tiempo al backend de actualizar el estado
        setTimeout(() => {
          onSensoresUpdate(false);
        }, 500);
      }
    } catch (error: any) {
      setIsCalculatingStrains(false);
      showError(error.response?.data?.detail || 'Error al iniciar el cálculo de strains');
      setStrainsStatus({
        calculados: false,
        mensaje: error.response?.data?.detail || 'Error al iniciar el cálculo',
      });
    }
  };

  // Resetear estado de strains FOS (manual recovery)
  const handleResetStrainsStatus = async () => {
    try {
      await apiService.updateEnsayo(ensayoId, {
        fos_strain_calculation_status: 'ERROR'
      });
      showSuccess('Estado de cálculo reiniciado. Puede intentar calcular de nuevo.');
      if (onSensoresUpdate) onSensoresUpdate();
    } catch (error: any) {
      showError('Error al reiniciar el estado');
    }
  };
  
  // Cargar CTE y dimensiones de la viga del ensayo al montar
  useEffect(() => {
    const loadEnsayoData = async () => {
      try {
        const ensayo = await apiService.getEnsayo(ensayoId);
        setEnsayoCte(ensayo.cte_substrate);
        setCteValue(ensayo.cte_substrate?.toString() || '');
        setEnsayoBeamDimensions({
          length: ensayo.beam_length,
          width: ensayo.beam_width,
          height: ensayo.beam_height,
        });
        setBeamLengthValue(ensayo.beam_length?.toString() || '');
        setBeamWidthValue(ensayo.beam_width?.toString() || '');
        setBeamHeightValue(ensayo.beam_height?.toString() || '');
      } catch (error) {
        console.error('Error al cargar datos del ensayo:', error);
      }
    };
    loadEnsayoData();
  }, [ensayoId]);
  
  // Abrir diálogo para introducir l_FAL y coordenadas
  const handleOpenLFalDialog = (sensor: Sensor) => {
    setSensorFALSeleccionado(sensor);
    setLFalValue(sensor.l_fal?.toString() || '');
    setPositionX(sensor.position_x?.toString() || '');
    setPositionY(sensor.position_y?.toString() || '');
    setPositionZ(sensor.position_z?.toString() || '');
    setLFalDialogOpen(true);
  };
  
  // Guardar l_FAL y coordenadas
  const handleSaveLFal = async () => {
    if (!sensorFALSeleccionado) return;
    
    const lFal = parseFloat(lFalValue);
    if (isNaN(lFal) || lFal <= 0) {
      showError('La longitud de anclaje (l_FAL) debe ser un número positivo');
      return;
    }
    
    // Validar coordenadas (opcionales pero si se proporcionan deben ser números válidos)
    const posX = positionX ? parseFloat(positionX) : null;
    const posY = positionY ? parseFloat(positionY) : null;
    const posZ = positionZ ? parseFloat(positionZ) : null;
    
    if (positionX && (isNaN(posX as number) || posX === null)) {
      showError('La coordenada X debe ser un número válido');
      return;
    }
    if (positionY && (isNaN(posY as number) || posY === null)) {
      showError('La coordenada Y debe ser un número válido');
      return;
    }
    if (positionZ && (isNaN(posZ as number) || posZ === null)) {
      showError('La coordenada Z debe ser un número válido');
      return;
    }
    
    setIsSavingLFal(true);
    try {
      const updateData: Partial<Sensor> = {
        l_fal: lFal,
        position_x: posX,
        position_y: posY,
        position_z: posZ,
      };
      
      await apiService.updateSensor(ensayoId, sensorFALSeleccionado.id, updateData);
      showSuccess(`Configuración guardada: l_FAL=${lFal} m${posX !== null ? `, X=${posX} mm` : ''}${posY !== null ? `, Y=${posY} mm` : ''}${posZ !== null ? `, Z=${posZ} mm` : ''}`);
      setLFalDialogOpen(false);
      setSensorFALSeleccionado(null);
      setLFalValue('');
      setPositionX('');
      setPositionY('');
      setPositionZ('');
      if (onSensoresUpdate) {
        onSensoresUpdate();
      }
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Error al guardar la configuración');
    } finally {
      setIsSavingLFal(false);
    }
  };
  
  // Abrir diálogo para introducir CTE del ensayo
  const handleOpenCteDialog = () => {
    setCteValue(ensayoCte?.toString() || '');
    setCteDialogOpen(true);
  };
  
  // Guardar CTE del ensayo
  const handleSaveCte = async () => {
    const cte = parseFloat(cteValue);
    if (isNaN(cte) || cte <= 0) {
      showError('El coeficiente de expansión térmica (CTE) debe ser un número positivo');
      return;
    }
    
    // Validar dimensiones de la viga (opcionales pero si se proporcionan deben ser números válidos)
    const beamLength = beamLengthValue ? parseFloat(beamLengthValue) : null;
    const beamWidth = beamWidthValue ? parseFloat(beamWidthValue) : null;
    const beamHeight = beamHeightValue ? parseFloat(beamHeightValue) : null;
    
    if (beamLengthValue && (isNaN(beamLength as number) || beamLength === null || beamLength <= 0)) {
      showError('La longitud de la viga debe ser un número positivo');
      return;
    }
    if (beamWidthValue && (isNaN(beamWidth as number) || beamWidth === null || beamWidth <= 0)) {
      showError('El ancho de la viga debe ser un número positivo');
      return;
    }
    if (beamHeightValue && (isNaN(beamHeight as number) || beamHeight === null || beamHeight <= 0)) {
      showError('El canto de la viga debe ser un número positivo');
      return;
    }
    
    setIsSavingCte(true);
    try {
      const updateData: any = {
        cte_substrate: cte,
      };
      
      if (beamLength !== null) {
        updateData.beam_length = beamLength;
      }
      if (beamWidth !== null) {
        updateData.beam_width = beamWidth;
      }
      if (beamHeight !== null) {
        updateData.beam_height = beamHeight;
      }
      
      const updatedEnsayo = await apiService.updateEnsayo(ensayoId, updateData);
      // Actualizar el estado con el valor retornado del servidor para asegurar consistencia
      setEnsayoCte(updatedEnsayo.cte_substrate);
      setEnsayoBeamDimensions({
        length: updatedEnsayo.beam_length,
        width: updatedEnsayo.beam_width,
        height: updatedEnsayo.beam_height,
      });
      showSuccess(`Configuración guardada: CTE=${updatedEnsayo.cte_substrate} με.°C⁻¹${beamLength !== null ? `, Longitud=${beamLength} mm` : ''}${beamWidth !== null ? `, Ancho=${beamWidth} mm` : ''}${beamHeight !== null ? `, Canto=${beamHeight} mm` : ''}`);
      setCteDialogOpen(false);
      if (onSensoresUpdate) {
        onSensoresUpdate();
      }
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Error al guardar la configuración');
    } finally {
      setIsSavingCte(false);
    }
  };
  
  // Verificar si hay sensores FOS configurados (Long-Gauge y/o Short-Gauge)
  // Los Short-Gauge no requieren CTE ni l_FAL, solo un sensor FOS asociado
  // Los Long-Gauge requieren CTE del ensayo y l_FAL configurado
  const verificarStrainsFOS = useMemo(() => {
    const sensoresFOS = sensores.filter(s => {
      const tipo = s.tipo_manual || s.tipo;
      return tipo === 'fibra_optica';
    });
    
    // Detectar si hay datos precalculados
    const sensoresPrecalculados = sensoresFOS.filter(s => s.has_precalculated_strain === true);
    const hasPrecalculatedData = sensoresPrecalculados.length > 0;
    
    // Filtrar solo sensores con sensor FOS asociado para cálculos
    const sensoresFOSAsociados = sensoresFOS.filter(s => s.fos_sensor_id && s.fos_sensor);
    
    // Separar por tipología
    const sensoresLongGauge = sensoresFOSAsociados.filter(s => {
      if (!s.fos_sensor) return false;
      const tipologia = getTipologiaFromProductType(s.fos_sensor.product_type);
      return tipologia === 'Long-Gauge';
    });
    
    const sensoresShortGauge = sensoresFOSAsociados.filter(s => {
      if (!s.fos_sensor) return false;
      const tipologia = getTipologiaFromProductType(s.fos_sensor.product_type);
      return tipologia === 'Short-Gauge';
    });
    
    // Validaciones para Long-Gauge
    const longGaugeConLFal = sensoresLongGauge.filter(s => s.l_fal !== null && s.l_fal !== undefined);
    const longGaugeFaltaLFal = sensoresLongGauge.filter(s => s.l_fal === null || s.l_fal === undefined);
    const tieneCte = ensayoCte !== null && ensayoCte !== undefined;
    
    // Long-Gauge está listo si todos tienen l_FAL y hay CTE (o no hay Long-Gauge)
    const longGaugeListo = sensoresLongGauge.length === 0 || 
      (longGaugeFaltaLFal.length === 0 && tieneCte);
    
    // Short-Gauge siempre está listo si tiene sensor FOS asociado (ya filtrado arriba)
    const shortGaugeListo = true;
    
    // Hay sensores para calcular si hay al menos uno de cualquier tipo
    const haySensoresParaCalcular = sensoresFOSAsociados.length > 0 || hasPrecalculatedData;
    
    // Se puede calcular si:
    // 1. Hay al menos un sensor FOS con sensor asociado
    // 2. Si hay Long-Gauge: todos deben tener l_FAL y el ensayo debe tener CTE
    // 3. Si solo hay Short-Gauge: siempre se puede calcular
    // 4. Si hay datos precalculados: mostrar aviso y permitir recalcular
    const puedeCalcular = (sensoresFOSAsociados.length > 0 && longGaugeListo && shortGaugeListo) || hasPrecalculatedData;
    
    // Generar mensaje descriptivo
    let mensaje = '';
    if (hasPrecalculatedData) {
      // Mensaje especial para datos precalculados
      mensaje = `⚡ Hay ${sensoresPrecalculados.length} sensor(es) FOS con datos de strain precalculados desde el Excel. Si recalculas, se sobrescribirán los valores actuales.`;
    } else if (!sensoresFOSAsociados.length) {
      mensaje = 'No hay sensores FOS con sensor de la base de datos asociado.';
    } else if (sensoresLongGauge.length > 0 && longGaugeFaltaLFal.length > 0) {
      // Hay Long-Gauge sin l_FAL
      const msgShortGauge = sensoresShortGauge.length > 0 
        ? ` Hay ${sensoresShortGauge.length} Short-Gauge listo(s).` 
        : '';
      mensaje = `Configura l_FAL para ${longGaugeFaltaLFal.length} sensor(es) Long-Gauge.${msgShortGauge}`;
    } else if (sensoresLongGauge.length > 0 && !tieneCte) {
      // Hay Long-Gauge listos pero falta CTE
      const msgShortGauge = sensoresShortGauge.length > 0 
        ? ` Los ${sensoresShortGauge.length} Short-Gauge pueden calcularse sin CTE.` 
        : '';
      mensaje = `Configura el CTE del ensayo para los ${sensoresLongGauge.length} sensor(es) Long-Gauge.${msgShortGauge}`;
    } else if (puedeCalcular) {
      // Todo listo para calcular
      const partes = [];
      if (sensoresLongGauge.length > 0) {
        partes.push(`${sensoresLongGauge.length} Long-Gauge`);
      }
      if (sensoresShortGauge.length > 0) {
        partes.push(`${sensoresShortGauge.length} Short-Gauge`);
      }
      mensaje = `Hay ${partes.join(' y ')} sensor(es) FOS configurado(s). Usa el botón "Calcular strains FOS" para calcularlos.`;
    }
    
    return {
      calculados: puedeCalcular,
      mensaje,
      // Información adicional para debug/UI
      longGauge: { total: sensoresLongGauge.length, listos: longGaugeConLFal.length },
      shortGauge: { total: sensoresShortGauge.length, listos: sensoresShortGauge.length },
      hasPrecalculatedData,
      precalculatedCount: sensoresPrecalculados.length,
    };
  }, [sensores, ensayoCte]);

  // Ordenar tipos por cantidad de sensores (más a menos)
  const tiposOrdenados = useMemo(() => {
    return Object.entries(sensoresPorTipo)
      .filter(([_, sensores]) => sensores.length > 0)
      .sort(([_, a], [__, b]) => b.length - a.length)
      .map(([tipo]) => tipo as TipoSensor);
  }, [sensoresPorTipo]);

  const parseMetadatos = (metadatosJson: string | null): Record<string, string> => {
    if (!metadatosJson) return {};
    try {
      return JSON.parse(metadatosJson);
    } catch {
      return {};
    }
  };

  const toggleRowExpansion = (sensorId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sensorId)) {
        newSet.delete(sensorId);
      } else {
        newSet.add(sensorId);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">Cargando metadatos...</Typography>
      </Box>
    );
  }

  if (sensores.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No hay sensores disponibles. Procesa el archivo de sensores para ver los metadatos.
        </Typography>
      </Box>
    );
  }

  // Si solo hay un tipo, no mostrar tabs
  const showTabs = tiposOrdenados.length > 1;

  return (
    <Box>
      {/* Resumen general */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {sensores.length} sensor(es) total
        </Typography>
        {tiposOrdenados.map((tipo) => {
          const count = sensoresPorTipo[tipo]?.length || 0;
          if (count === 0) return null;
          return (
            <Chip
              key={tipo}
              label={`${tipoLabels[tipo]}: ${count}`}
              size="small"
              sx={{
                bgcolor: alpha(tipoColors[tipo], 0.1),
                color: tipoColors[tipo],
                border: `1px solid ${alpha(tipoColors[tipo], 0.3)}`,
                fontWeight: 500,
              }}
            />
          );
        })}
      </Box>
      
      {/* Mensaje sobre desplazamientos y botón para calcular */}
      {sensoresPorTipo['lvdt'] && sensoresPorTipo['lvdt'].length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity={verificarDesplazamientos.calculados ? 'info' : 'warning'}
            action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={(isCalculating || displacementStatus === 'PROCESSING') ? <CircularProgress size={16} /> : <CalculateIcon />}
                onClick={handleCalcularDesplazamientos}
                disabled={isCalculating || displacementStatus === 'PROCESSING' || displacementStatus === 'PENDING' || !verificarDesplazamientos.calculados || !sensorReferenciaLVDT}
              >
                {(isCalculating || displacementStatus === 'PROCESSING' || displacementStatus === 'PENDING') ? 'Calculando...' : 'Calcular desplazamientos'}
              </Button>
              {(displacementStatus === 'PROCESSING' || displacementStatus === 'PENDING') && (
                <Tooltip title="Reiniciar estado si se queda atascado">
                  <IconButton 
                    size="small" 
                    onClick={handleResetDesplazamientosStatus}
                    color="warning"
                  >
                    <RestartAltIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            }
          >
            {sensorReferenciaLVDT ? (
              verificarDesplazamientos.calculados ? (
                desplazamientosStatus?.calculados 
                  ? desplazamientosStatus.mensaje
                  : verificarDesplazamientos.mensaje
              ) : (
                verificarDesplazamientos.mensaje
              )
            ) : (
              'No se ha identificado un sensor de referencia LVDT (debe tener "_REF" en el nombre)'
            )}
          </Alert>
        </Box>
      )}
      
      {/* Mensaje sobre strains FOS y botón para calcular */}
      {sensoresPorTipo['fibra_optica'] && sensoresPorTipo['fibra_optica'].length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity={verificarStrainsFOS.hasPrecalculatedData ? 'success' : (verificarStrainsFOS.calculados ? 'info' : 'warning')}
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    Simplificado
                  </Typography>
                  <Switch
                    checked={!simplifiedMode}
                    onChange={(e) => setSimplifiedMode(!e.target.checked)}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    Completo
                  </Typography>
                </Box>
                <Tooltip title={ensayoCte ? `CTE del ensayo: ${ensayoCte} με.°C⁻¹` : 'Configurar coeficiente de expansión térmica del ensayo'}>
                  <IconButton
                    size="small"
                    onClick={handleOpenCteDialog}
                    color={ensayoCte ? 'primary' : 'default'}
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Información sobre el cálculo de strains FOS">
                  <IconButton
                    size="small"
                    onClick={() => setShowStrainsInfo(true)}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={(isCalculatingStrains || fosStatus === 'PROCESSING' || fosStatus === 'PENDING') ? <CircularProgress size={16} /> : <CalculateIcon />}
                  onClick={handleCalcularStrainsFOS}
                  disabled={isCalculatingStrains || !verificarStrainsFOS.calculados || fosStatus === 'PROCESSING' || fosStatus === 'PENDING'}
                >
                  {(isCalculatingStrains || fosStatus === 'PROCESSING' || fosStatus === 'PENDING') ? 'Calculando...' : 'Calcular strains FOS'}
                </Button>
                {(fosStatus === 'PROCESSING' || fosStatus === 'PENDING') && (
                  <Tooltip title="Reiniciar estado si se queda atascado">
                    <IconButton 
                      size="small" 
                      onClick={handleResetStrainsStatus}
                      color="warning"
                    >
                      <RestartAltIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            }
          >
            {verificarStrainsFOS.calculados ? (
              strainsStatus?.calculados 
                ? strainsStatus.mensaje
                : verificarStrainsFOS.mensaje
            ) : (
              verificarStrainsFOS.mensaje
            )}
          </Alert>
        </Box>
      )}

      {showTabs ? (
        <>
          {/* Tabs para diferentes tipos */}
          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Tabs
              value={selectedTab}
              onChange={(_, newValue) => setSelectedTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  minHeight: 48,
                  textTransform: 'none',
                  fontWeight: 500,
                },
              }}
            >
              {tiposOrdenados.map((tipo) => {
                const count = sensoresPorTipo[tipo]?.length || 0;
                return (
                  <Tab
                    key={tipo}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={tipoLabels[tipo]}
                          size="small"
                          sx={{
                            bgcolor: tipoColors[tipo],
                            color: 'white',
                            height: 20,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          ({count})
                        </Typography>
                      </Box>
                    }
                  />
                );
              })}
            </Tabs>
          </Paper>

          {/* Tab panels */}
          {tiposOrdenados.map((tipo, index) => (
            <TabPanel key={tipo} value={selectedTab} index={index}>
              <SensorTable
                sensores={sensoresPorTipo[tipo] || []}
                tipo={tipo}
                expandedRows={expandedRows}
                onToggleExpansion={toggleRowExpansion}
                parseMetadatos={parseMetadatos}
                ensayoId={ensayoId}
                sensorReferenciaLVDT={sensorReferenciaLVDT}
                onOpenLongitudDialog={handleOpenLongitudDialog}
                onOpenFOSDialog={handleOpenFOSDialog}
                onDesasociarFOS={handleDesasociarFOS}
                onOpenLFalDialog={handleOpenLFalDialog}
                onOpen3DDialog={(sensor) => {
                  setSensor3DSeleccionado(sensor);
                  setView3DDialogOpen(true);
                }}
                onSensoresUpdate={onSensoresUpdate}
                datosPorSensor={datosPorSensor}
                isCalculating={tipo === 'fibra_optica' 
                  ? (isCalculatingStrains || fosStatus === 'PROCESSING' || fosStatus === 'PENDING')
                  : (isCalculating || displacementStatus === 'PROCESSING' || displacementStatus === 'PENDING')}
                isCompleted={tipo === 'fibra_optica' 
                  ? (fosStatus === 'COMPLETED' || strainsStatus?.calculados)
                  : (displacementStatus === 'COMPLETED' || desplazamientosStatus?.calculados)}
              />
            </TabPanel>
          ))}
        </>
      ) : (
        // Si solo hay un tipo, mostrar directamente la tabla
        <SensorTable
          sensores={sensoresPorTipo[tiposOrdenados[0]] || []}
          tipo={tiposOrdenados[0]}
          expandedRows={expandedRows}
          onToggleExpansion={toggleRowExpansion}
          parseMetadatos={parseMetadatos}
          ensayoId={ensayoId}
          sensorReferenciaLVDT={sensorReferenciaLVDT}
          onOpenLongitudDialog={handleOpenLongitudDialog}
          onOpenFOSDialog={handleOpenFOSDialog}
          onDesasociarFOS={handleDesasociarFOS}
          onOpenLFalDialog={handleOpenLFalDialog}
          onOpen3DDialog={(sensor) => {
            setSensor3DSeleccionado(sensor);
            setView3DDialogOpen(true);
          }}
          onSensoresUpdate={onSensoresUpdate}
          datosPorSensor={datosPorSensor}
          isCalculating={tiposOrdenados[0] === 'fibra_optica' 
            ? (isCalculatingStrains || fosStatus === 'PROCESSING' || fosStatus === 'PENDING')
            : (isCalculating || displacementStatus === 'PROCESSING' || displacementStatus === 'PENDING')}
          isCompleted={tiposOrdenados[0] === 'fibra_optica'
            ? (fosStatus === 'COMPLETED' || strainsStatus?.calculados)
            : (displacementStatus === 'COMPLETED' || desplazamientosStatus?.calculados)}
        />
      )}
      
      {/* Diálogo para asociar sensor FOS */}
      <CorporateDialog
        open={fosDialogOpen}
        onClose={handleCloseFOSDialog}
        title="Asociar Sensor FOS"
        maxWidth="lg"
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          {isLoadingFOS ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              {/* Filtros */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Tipología</InputLabel>
                  <Select
                    value={fosTipologiaFilter}
                    label="Tipología"
                    onChange={(e) => setFosTipologiaFilter(e.target.value)}
                  >
                    <MenuItem value="all">Todas</MenuItem>
                    <MenuItem value="Long-Gauge">Long-Gauge</MenuItem>
                    <MenuItem value="Short-Gauge">Short-Gauge</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Gauge Length</InputLabel>
                  <Select
                    value={fosGLFilter}
                    label="Gauge Length"
                    onChange={(e) => setFosGLFilter(e.target.value)}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {uniqueGaugeLengths.map((gl) => (
                      <MenuItem key={gl} value={gl}>
                        {gl}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <CorporateTextField
                  label="Buscar sensor FOS"
                  value={fosSearchTerm}
                  onChange={(e) => setFosSearchTerm(e.target.value)}
                  sx={{ flex: 1 }}
                  placeholder="Buscar por número de serie, código de cliente o tipo de producto..."
                />
              </Box>

              {/* Tabla de sensores */}
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Número de Serie</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Tipología</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Tipo de Producto</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Código Cliente</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>GL</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Rango Longitud de Onda</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fosSensors
                      .filter((fos: FOSSensor) => {
                        // Calcular tipología basada en product_type
                        const tipologia = getTipologiaFromProductType(fos.product_type);
                        // Filtro por tipología
                        if (fosTipologiaFilter !== 'all' && tipologia !== fosTipologiaFilter) {
                          return false;
                        }
                        
                        // Filtro por GL
                        if (fosGLFilter && fos.gauge_length !== fosGLFilter) {
                          return false;
                        }

                        // Filtro por búsqueda
                        if (!fosSearchTerm) return true;
                        const search = fosSearchTerm.toLowerCase();
                        return (
                          fos.serial_number.toLowerCase().includes(search) ||
                          (fos.customer_code && fos.customer_code.toLowerCase().includes(search)) ||
                          fos.product_type.toLowerCase().includes(search)
                        );
                      })
                      .map((fos: FOSSensor) => {
                        const tipologia = getTipologiaFromProductType(fos.product_type);
                        return (
                        <TableRow
                          key={fos.id}
                          onClick={() => handleAsociarFOS(fos.id)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                            },
                            bgcolor: sensorFOSSeleccionado?.fos_sensor_id === fos.id
                              ? alpha(theme.palette.primary.main, 0.1)
                              : 'transparent',
                            borderLeft: sensorFOSSeleccionado?.fos_sensor_id === fos.id
                              ? `4px solid ${theme.palette.primary.main}`
                              : '4px solid transparent',
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {fos.serial_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tipologia}
                              size="small"
                              color={tipologia === 'Long-Gauge' ? 'primary' : 'secondary'}
                              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {fos.product_type}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {fos.customer_code || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {fos.gauge_length || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {fos.wavelength_range || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    {fosSensors.filter((fos: FOSSensor) => {
                      const tipologia = getTipologiaFromProductType(fos.product_type);
                      if (fosTipologiaFilter !== 'all' && tipologia !== fosTipologiaFilter) {
                        return false;
                      }
                      if (!fosSearchTerm) return true;
                      const search = fosSearchTerm.toLowerCase();
                      return (
                        fos.serial_number.toLowerCase().includes(search) ||
                        (fos.customer_code && fos.customer_code.toLowerCase().includes(search)) ||
                        fos.product_type.toLowerCase().includes(search)
                      );
                    }).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No se encontraron sensores FOS
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </Box>
      </CorporateDialog>

      {/* Diálogo para introducir longitud de medición */}
      <CorporateDialog
        open={longitudDialogOpen}
        onClose={() => {
          setLongitudDialogOpen(false);
          setSensorSeleccionado(null);
          setLongitudValue('');
        }}
        title="Longitud de medición (Lmed,lvdt)"
        icon={<SettingsIcon />}
        maxWidth="sm"
        fullWidth
        actions={
          <>
            <CorporateButton
              onClick={() => {
                setLongitudDialogOpen(false);
                setSensorSeleccionado(null);
                setLongitudValue('');
              }}
              color="inherit"
            >
              Cancelar
            </CorporateButton>
            <CorporateButton
              onClick={handleSaveLongitud}
              disabled={isSavingLongitud || !longitudValue}
              colorVariant="accent"
            >
              {isSavingLongitud ? 'Guardando...' : 'Guardar'}
            </CorporateButton>
          </>
        }
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Sensor: <strong>{sensorSeleccionado?.nombre}</strong>
          </Typography>
          <CorporateTextField
            label="Longitud de medición (mm)"
            type="number"
            value={longitudValue}
            onChange={(e) => setLongitudValue(e.target.value)}
            placeholder="Ej: 30, 50, 100, 300"
            fullWidth
            required
            inputProps={{ min: 0, step: 0.01 }}
            helperText="Longitud total que puede medir el sensor LVDT (normalmente entre 30 mm y 300 mm)"
          />
          {sensorReferenciaLVDT && !sensorSeleccionado?.sensor_referencia_id && (
            <Alert severity="info">
              Se asignará automáticamente el sensor de referencia: <strong>{sensorReferenciaLVDT.nombre}</strong>
            </Alert>
          )}
        </Stack>
      </CorporateDialog>
      
      {/* Diálogo para introducir l_FAL y coordenadas (FOS) */}
      <CorporateDialog
        open={lFalDialogOpen}
        onClose={() => {
          setLFalDialogOpen(false);
          setSensorFALSeleccionado(null);
          setLFalValue('');
          setPositionX('');
          setPositionY('');
          setPositionZ('');
        }}
        title="Configuración del sensor FOS"
        icon={<SettingsIcon />}
        maxWidth="md"
        fullWidth
        actions={
          <>
            <CorporateButton
              onClick={() => {
                setLFalDialogOpen(false);
                setSensorFALSeleccionado(null);
                setLFalValue('');
                setPositionX('');
                setPositionY('');
                setPositionZ('');
              }}
              color="inherit"
            >
              Cancelar
            </CorporateButton>
            <CorporateButton
              onClick={handleSaveLFal}
              disabled={isSavingLFal || !lFalValue}
              colorVariant="accent"
            >
              {isSavingLFal ? 'Guardando...' : 'Guardar'}
            </CorporateButton>
          </>
        }
      >
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            Sensor: <strong>{sensorFALSeleccionado?.nombre}</strong>
          </Typography>
          
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Longitud de anclaje
            </Typography>
            <CorporateTextField
              label="Longitud de anclaje (l_FAL) [m]"
              type="number"
              value={lFalValue}
              onChange={(e) => setLFalValue(e.target.value)}
              placeholder="Ej: 0.5, 1.0, 2.0"
              fullWidth
              required
              inputProps={{ min: 0, step: 0.001 }}
              helperText="Distancia entre centros de mounting brackets del sensor FOS Long-Gauge (en metros)"
            />
          </Box>
          
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Coordenadas espaciales (opcional)
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Posición de sensores:</strong> La posición de todos los sensores debe ser introducida por el usuario.
                <br />• <strong>X (longitudinal):</strong> Posición media del sensor
                <br />• <strong>Y (transversal):</strong> Posición transversal (menos relevante, excepto para sistemas de varias vigas como un forjado)
                <br />• <strong>Z (vertical):</strong> Posición del sensor en relación al canto de la sección
              </Typography>
            </Alert>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
              <CorporateTextField
                label="X - Posición longitudinal [mm]"
                type="number"
                value={positionX}
                onChange={(e) => setPositionX(e.target.value)}
                placeholder="Ej: 100, 200"
                fullWidth
                inputProps={{ step: 0.01 }}
                helperText="Posición media del sensor"
              />
              <CorporateTextField
                label="Y - Posición transversal [mm]"
                type="number"
                value={positionY}
                onChange={(e) => setPositionY(e.target.value)}
                placeholder="Ej: 0, 50"
                fullWidth
                inputProps={{ step: 0.01 }}
                helperText="Posición transversal"
              />
              <CorporateTextField
                label="Z - Posición vertical [mm]"
                type="number"
                value={positionZ}
                onChange={(e) => setPositionZ(e.target.value)}
                placeholder="Ej: 0, 25"
                fullWidth
                inputProps={{ step: 0.01 }}
                helperText="Relación al canto de la sección"
              />
            </Box>
          </Box>
        </Stack>
      </CorporateDialog>
      
      {/* Diálogo para introducir CTE del ensayo */}
      <CorporateDialog
        open={cteDialogOpen}
        onClose={() => {
          setCteDialogOpen(false);
          setCteValue(ensayoCte?.toString() || '');
        }}
        title="Configuración del ensayo FOS"
        icon={<SettingsIcon />}
        maxWidth="md"
        fullWidth
        actions={
          <>
            <CorporateButton
              onClick={() => {
                setCteDialogOpen(false);
                setCteValue(ensayoCte?.toString() || '');
                setBeamLengthValue(ensayoBeamDimensions.length?.toString() || '');
                setBeamWidthValue(ensayoBeamDimensions.width?.toString() || '');
                setBeamHeightValue(ensayoBeamDimensions.height?.toString() || '');
              }}
              color="inherit"
            >
              Cancelar
            </CorporateButton>
            <CorporateButton
              onClick={handleSaveCte}
              disabled={isSavingCte || !cteValue}
              colorVariant="accent"
            >
              {isSavingCte ? 'Guardando...' : 'Guardar'}
            </CorporateButton>
          </>
        }
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Coeficiente de expansión térmica del sustrato
            </Typography>
            <CorporateTextField
              label="CTE del sustrato (με.°C⁻¹)"
              type="number"
              value={cteValue}
              onChange={(e) => setCteValue(e.target.value)}
              placeholder="Ej: 10.5, 12.0"
              fullWidth
              required
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Coeficiente de expansión térmica del material del sustrato. Aplica a todos los sensores FOS del ensayo."
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              Este valor se aplicará a todos los sensores FOS Long-Gauge del ensayo al calcular los strains.
            </Alert>
          </Box>
          
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Dimensiones de la viga (para visualización 3D)
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Configure las dimensiones de la viga para la visualización 3D de la posición de los sensores.
                Estos valores se utilizarán para representar la viga en el visualizador 3D.
              </Typography>
            </Alert>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
              <CorporateTextField
                label="Longitud [mm]"
                type="number"
                value={beamLengthValue}
                onChange={(e) => setBeamLengthValue(e.target.value)}
                placeholder="Ej: 2000, 3000"
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Longitud de la viga"
              />
              <CorporateTextField
                label="Ancho [mm]"
                type="number"
                value={beamWidthValue}
                onChange={(e) => setBeamWidthValue(e.target.value)}
                placeholder="Ej: 200, 300"
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Ancho de la viga"
              />
              <CorporateTextField
                label="Canto [mm]"
                type="number"
                value={beamHeightValue}
                onChange={(e) => setBeamHeightValue(e.target.value)}
                placeholder="Ej: 300, 400"
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Canto (altura) de la viga"
              />
            </Box>
          </Box>
        </Stack>
      </CorporateDialog>
      
      {/* Modal informativo sobre cálculo de strains FOS */}
      <Dialog
        open={showStrainsInfo}
        onClose={() => setShowStrainsInfo(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Información sobre el cálculo de strains FOS
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            El cálculo de microstrains para sensores FOS utiliza diferentes fórmulas según el tipo de sensor.
            El sistema detecta automáticamente el tipo y aplica la fórmula correspondiente.
          </Typography>
          
          {/* Long-Gauge */}
          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600, color: 'primary.main' }}>
            Sensores Long-Gauge (SC-01/T)
          </Typography>
          <Typography variant="body2" paragraph>
            Calcula strain considerando todos los componentes térmicos. Recomendado para ensayos largos
            donde los efectos térmicos son significativos.
          </Typography>
          <Box component="code" sx={{ 
            display: 'block', 
            p: 1, 
            bgcolor: 'grey.100', 
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            mb: 2,
            border: '1px solid',
            borderColor: 'grey.300'
          }}>
            Δε = ((Δλ - B × ΔT) / (A × Δl)) + (ΔT × CTE_SS304 × Δl²) - (ΔT × CTE_substrate)
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.8rem' }}>
            <strong>Requisitos:</strong> Variables 'strain' y 'temp', λ₀, T₀, l_FAL, CTE_substrate, coeficientes A y B
          </Typography>
          
          {/* Short-Gauge */}
          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600, color: 'secondary.main' }}>
            Sensores Short-Gauge (MS-03, SGCalsens)
          </Typography>
          <Typography variant="body2" paragraph>
            Los sensores Short-Gauge utilizan una fórmula simplificada sin compensación de temperatura.
            Son ideales para mediciones puntuales donde los efectos térmicos son despreciables.
          </Typography>
          <Box component="code" sx={{ 
            display: 'block', 
            p: 1, 
            bgcolor: 'grey.100', 
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            mb: 2,
            border: '1px solid',
            borderColor: 'grey.300'
          }}>
            ε = (λti - λt0) × (10³ / 1.2)
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            <strong>Requisitos:</strong> Solo variable 'strain' (wavelength), λ₀ (calculado automáticamente)
          </Typography>
          
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Nota:</strong> Los sensores SGCalsens y MS-03 no requieren parámetros de calibración 
              (A, B, CTE, TS1-3, λ_T,ref) ya que utilizan la fórmula simplificada.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStrainsInfo(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal de visualización 3D */}
      <Dialog
        open={view3DDialogOpen}
        onClose={() => {
          setView3DDialogOpen(false);
          setSensor3DSeleccionado(null);
        }}
        TransitionProps={{
          onEntered: () => {
            // Dibujar cuando el diálogo esté completamente abierto
            if (sensor3DSeleccionado) {
              setTimeout(() => {
                drawCanvas3D();
              }, 100);
            }
          }
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Visualización 3D - Posición del sensor
        </DialogTitle>
        <DialogContent>
          {sensor3DSeleccionado && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sensor: <strong>{sensor3DSeleccionado.nombre}</strong>
              </Typography>
              {sensor3DSeleccionado.position_x !== null || 
               sensor3DSeleccionado.position_y !== null || 
               sensor3DSeleccionado.position_z !== null ? (
                <Box>
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                      Coordenadas:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {sensor3DSeleccionado.position_x !== null && (
                        <Typography variant="body2">
                          X: <strong>{Number(sensor3DSeleccionado.position_x).toFixed(2)} mm</strong> (longitudinal)
                        </Typography>
                      )}
                      {sensor3DSeleccionado.position_y !== null && (
                        <Typography variant="body2">
                          Y: <strong>{Number(sensor3DSeleccionado.position_y).toFixed(2)} mm</strong> (transversal)
                        </Typography>
                      )}
                      {sensor3DSeleccionado.position_z !== null && (
                        <Typography variant="body2">
                          Z: <strong>{Number(sensor3DSeleccionado.position_z).toFixed(2)} mm</strong> (vertical - canto)
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Visualización 3D interactiva con Canvas */}
                  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, bgcolor: 'background.paper', position: 'relative' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Visualización 3D interactiva de la viga
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Zoom in">
                          <IconButton size="small" onClick={() => handleZoom3D(0.2)}>
                            <ZoomInIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Zoom out">
                          <IconButton size="small" onClick={() => handleZoom3D(-0.2)}>
                            <ZoomOutIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Rotar vista">
                          <IconButton 
                            size="small" 
                            onClick={() => setRotation3D(prev => ({ ...prev, y: prev.y + Math.PI / 4 }))}
                          >
                            <RotateRightIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Resetear vista">
                          <IconButton size="small" onClick={handleReset3D}>
                            <RestartAltIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Box sx={{ position: 'relative', border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                      <canvas
                        ref={canvas3DRef}
                        onMouseDown={handleMouseDown3D}
                        onMouseMove={handleMouseMove3D}
                        onMouseUp={handleMouseUp3D}
                        onMouseLeave={handleMouseUp3D}
                        onWheel={handleWheel3D}
                        style={{
                          width: '100%',
                          height: '500px',
                          display: 'block',
                          cursor: isDragging3D ? 'grabbing' : 'grab',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 10,
                          left: 10,
                          bgcolor: alpha(theme.palette.background.paper, 0.9),
                          p: 1,
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                          <PanToolIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                          Arrastra para mover | Rueda para zoom
                        </Typography>
                      </Box>
                    </Box>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Controles:</strong> Arrastra el canvas para mover la vista, usa la rueda del ratón para hacer zoom.
                        El punto rojo indica la posición del sensor según las coordenadas introducidas.
                      </Typography>
                    </Alert>
                  </Box>
                </Box>
              ) : (
                <Alert severity="warning">
                  Este sensor no tiene coordenadas configuradas. Configura las coordenadas para visualizar la posición.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setView3DDialogOpen(false);
            setSensor3DSeleccionado(null);
          }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface SensorTableProps {
  sensores: Sensor[];
  tipo: TipoSensor;
  expandedRows: Set<string>;
  onToggleExpansion: (sensorId: string) => void;
  parseMetadatos: (json: string | null) => Record<string, string>;
  ensayoId: string;
  sensorReferenciaLVDT: Sensor | undefined;
  onOpenLongitudDialog: (sensor: Sensor) => void;
  onOpenFOSDialog?: (sensor: Sensor) => void;
  onDesasociarFOS?: (sensor: Sensor) => void;
  onOpenLFalDialog?: (sensor: Sensor) => void;
  onSensoresUpdate?: () => void;
  datosPorSensor?: Record<string, SensorData[]>;
  onOpen3DDialog?: (sensor: Sensor) => void;
  isCalculating?: boolean;
  isCompleted?: boolean;
}

const SensorTable: React.FC<SensorTableProps> = ({
  sensores,
  tipo,
  expandedRows,
  onToggleExpansion,
  parseMetadatos,
  ensayoId,
  sensorReferenciaLVDT,
  onOpenLongitudDialog,
  onOpenFOSDialog,
  onDesasociarFOS,
  onOpenLFalDialog,
  onOpen3DDialog,
  datosPorSensor = {},
  isCalculating = false,
  isCompleted = false,
}) => {
  const theme = useTheme();
  const tipoColor = tipoColors[tipo];
  
  // Determinar tipología basada en product_type (misma lógica que FOSSensorsPage)
  // SC-01/T: Long-Gauge, MS-03/SGCalsens: Short-Gauge
  const getTipologiaFromProductType = (productType: string): FOSTipologia => {
    if (!productType) return 'Long-Gauge';
    const pt = productType.toUpperCase();
    if (pt.includes('SC-01/T')) {
      return 'Long-Gauge';
    } else if (pt.includes('MS-03') || pt.includes('SGCALSENS')) {
      return 'Short-Gauge';
    }
    return 'Long-Gauge'; // Por defecto
  };
  
  // Determinar si es un sensor LVDT no-referencia
  const esLVDTNoReferencia = (sensor: Sensor) => {
    const tipoFinal = sensor.tipo_manual || sensor.tipo;
    return tipoFinal === 'lvdt' && !sensor.nombre.toUpperCase().includes('_REF');
  };
  
  // Verificar si un sensor tiene desplazamientos calculados
  const tieneDesplazamientosCalculados = (sensor: Sensor): boolean => {
    const datos = datosPorSensor[sensor.id] || [];
    // Verificar si hay al menos un dato con desplazamiento no nulo
    return datos.some(d => d.desplazamiento !== null && d.desplazamiento !== undefined);
  };

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        maxHeight: '70vh',
        overflow: 'auto',
        '& .MuiTable-root': {
          minWidth: 650,
        },
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 40, p: 1 }}></TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Nombre</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Tipo</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Unidades</TableCell>

            {tipo !== 'fibra_optica' && (
              <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Nombre Original</TableCell>
            )}
            {tipo === 'lvdt' && (
              <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>L. Medición</TableCell>
            )}
            {tipo === 'lvdt' && (
              <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Desplazamientos</TableCell>
            )}
            {tipo === 'lvdt' && (
              <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Acciones</TableCell>
            )}
            {tipo === 'fibra_optica' && (
              <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>Channel</TableCell>
            )}
            {tipo === 'fibra_optica' && (
              <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Modelo Sensor</TableCell>
            )}
            {tipo === 'fibra_optica' && (
              <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Tipología</TableCell>
            )}
            {tipo === 'fibra_optica' && (
              <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>L. Anclaje</TableCell>
            )}
            {tipo === 'fibra_optica' && (
              <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>λ₀ inicial</TableCell>
            )}
            {tipo === 'fibra_optica' && (
              <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>T₀ inicial</TableCell>
            )}
            {tipo === 'fibra_optica' && (
              <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Coordenadas</TableCell>
            )}
            {tipo === 'fibra_optica' && (
              <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Acciones</TableCell>
            )}
            <TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sensores.map((sensor) => {
            const isExpanded = expandedRows.has(sensor.id);
            const metadatos = parseMetadatos(sensor.metadatos_adicionales);
            const hasDetails = sensor.descripcion || Object.keys(metadatos).length > 0;
            const tipoFinal = sensor.tipo_manual || sensor.tipo;
            const tipoEditado = sensor.tipo_manual && sensor.tipo_manual !== sensor.tipo;

            // Verificar si tiene variables (FOS) o desplazamiento (LVDT)
            const tieneVariables = sensor.variables && sensor.variables.length > 0;
            const esLVDTConDesplazamiento = tipo === 'lvdt' && esLVDTNoReferencia(sensor) && 
              tieneDesplazamientosCalculados(sensor);
            const tieneSubFilas = tieneVariables || esLVDTConDesplazamiento;

            return (
              <React.Fragment key={sensor.id}>
                {/* Fila principal del sensor */}
                <TableRow
                  sx={{
                    '&:hover': {
                      bgcolor: alpha(tipoColor, 0.05),
                    },
                    '&:nth-of-type(even)': {
                      bgcolor: alpha(tipoColor, 0.02),
                    },
                    bgcolor: tieneSubFilas ? alpha(tipoColor, 0.03) : 'transparent',
                  }}
                >
                  <TableCell sx={{ p: 0.5 }}>
                    {(hasDetails || tieneSubFilas) && (
                      <IconButton
                        size="small"
                        onClick={() => onToggleExpansion(sensor.id)}
                        sx={{ p: 0.5 }}
                      >
                        {isExpanded ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                    {!hasDetails && !tieneSubFilas && <Box sx={{ width: 24 }} />}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {sensor.nombre}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={tipoLabels[tipoFinal]}
                        size="small"
                        sx={{
                          bgcolor: tipoColors[tipoFinal],
                          color: 'white',
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 500,
                        }}
                      />
                      {tipoEditado && (
                        <Tooltip title={`Tipo original: ${tipoLabels[sensor.tipo]}`}>
                          <EditIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {sensor.unidades ? (
                      <Chip
                        label={sensor.unidades}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>

                  {tipo !== 'fibra_optica' && (
                    <TableCell>
                      {sensor.nombre_columna_excel && sensor.nombre_columna_excel !== sensor.nombre ? (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {sensor.nombre_columna_excel}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {tipo === 'lvdt' && (
                    <TableCell>
                      {sensor.longitud_medicion ? (
                        <Chip
                          label={`${sensor.longitud_medicion} mm`}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            color: theme.palette.success.main,
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No configurado
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {tipo === 'lvdt' && (
                    <TableCell>
                      {esLVDTNoReferencia(sensor) ? (
                        (tieneDesplazamientosCalculados(sensor) || isCompleted) ? (
                          <Chip
                            label="Calculados"
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                              color: theme.palette.success.main,
                              fontWeight: 500,
                            }}
                          />
                        ) : (
                          isCalculating ? (
                            <Chip
                              label="Calculando..."
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                color: theme.palette.info.main,
                                animation: 'pulse 1.5s infinite ease-in-out',
                                '@keyframes pulse': {
                                  '0%': { opacity: 0.6 },
                                  '50%': { opacity: 1 },
                                  '100%': { opacity: 0.6 },
                                },
                              }}
                            />
                          ) : (
                            <Chip
                              label="No calculados"
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                color: theme.palette.warning.main,
                              }}
                            />
                          )
                        )
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {tipo === 'lvdt' && (
                    <TableCell>
                      {esLVDTNoReferencia(sensor) && (
                        <Tooltip title="Configurar longitud de medición (Lmed,lvdt)">
                          <IconButton
                            size="small"
                            onClick={() => onOpenLongitudDialog(sensor)}
                            sx={{ p: 0.5 }}
                          >
                            <SettingsIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {sensor.nombre.toUpperCase().includes('_REF') && (
                        <Chip
                          label="Referencia"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: theme.palette.primary.main,
                            color: 'white',
                          }}
                        />
                      )}
                    </TableCell>
                  )}
                  {tipo === 'fibra_optica' && (
                    <TableCell>
                      {/* Mostrar el channel del interrogador desde metadatos_adicionales */}
                      {metadatos?.channel ? (
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                          {metadatos.channel}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {tipo === 'fibra_optica' && (
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        {sensor.fos_sensor_id ? (
                          <>
                            {sensor.fos_sensor && (
                              <Tooltip title={`Asociado a: ${sensor.fos_sensor.serial_number} (${sensor.fos_sensor.product_type})`}>
                                <Chip
                                  label={sensor.fos_sensor.serial_number}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                    color: theme.palette.success.main,
                                    fontWeight: 500,
                                  }}
                                />
                              </Tooltip>
                            )}
                            <Tooltip title="Desasociar sensor FOS">
                              <IconButton
                                size="small"
                                onClick={() => onDesasociarFOS && onDesasociarFOS(sensor)}
                                sx={{ p: 0.5 }}
                              >
                                <LinkOffIcon sx={{ fontSize: 18, color: 'error.main' }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Tooltip title="Asociar con sensor FOS de la base de datos">
                            <IconButton
                              size="small"
                              onClick={() => onOpenFOSDialog && onOpenFOSDialog(sensor)}
                              sx={{ p: 0.5 }}
                            >
                              <LinkIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {tipo === 'fibra_optica' && (
                    <TableCell>
                      {sensor.fos_sensor ? (
                        <Chip
                          label={getTipologiaFromProductType(sensor.fos_sensor.product_type)}
                          size="small"
                          color={getTipologiaFromProductType(sensor.fos_sensor.product_type) === 'Long-Gauge' ? 'primary' : 'secondary'}
                          sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500 }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {tipo === 'fibra_optica' && (
                    <TableCell>
                      {sensor.fos_sensor && getTipologiaFromProductType(sensor.fos_sensor.product_type) === 'Long-Gauge' ? (
                        sensor.l_fal ? (
                          <Chip
                            label={`${Number(sensor.l_fal).toFixed(2)} m`}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                              color: theme.palette.success.main,
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No configurado
                          </Typography>
                        )
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {tipo === 'fibra_optica' && (
                    <TableCell>
                      {/* Mostrar λ₀ inicial de la variable strain si existe */}
                      {sensor.variables?.find(v => v.nombre_variable === 'strain')?.lambda_0_initial ? (
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                          {Number(sensor.variables.find(v => v.nombre_variable === 'strain')?.lambda_0_initial).toFixed(3)} nm
                        </Typography>
                      ) : isCalculating ? (
                        <Chip
                          label="Calculando..."
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: theme.palette.info.main,
                            animation: 'pulse 1.5s infinite ease-in-out',
                            '@keyframes pulse': {
                              '0%': { opacity: 0.6 },
                              '50%': { opacity: 1 },
                              '100%': { opacity: 0.6 },
                            },
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {tipo === 'fibra_optica' && (
                    <TableCell>
                      {/* Mostrar T₀ inicial de la variable temp si existe (en nm) */}
                      {sensor.variables?.find(v => v.nombre_variable === 'temp')?.t_0_initial ? (
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                          {Number(sensor.variables.find(v => v.nombre_variable === 'temp')?.t_0_initial).toFixed(3)} nm
                        </Typography>
                      ) : isCalculating ? (
                        <Chip
                          label="Calculando..."
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: theme.palette.info.main,
                            animation: 'pulse 1.5s infinite ease-in-out',
                            '@keyframes pulse': {
                              '0%': { opacity: 0.6 },
                              '50%': { opacity: 1 },
                              '100%': { opacity: 0.6 },
                            },
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {tipo === 'fibra_optica' && (
                    <TableCell>
                      {sensor.fos_sensor && getTipologiaFromProductType(sensor.fos_sensor.product_type) === 'Long-Gauge' ? (
                        sensor.position_x !== null || sensor.position_y !== null || sensor.position_z !== null ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                              {sensor.position_x !== null && (
                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                  X: {Number(sensor.position_x).toFixed(2)} mm
                                </Typography>
                              )}
                              {sensor.position_y !== null && (
                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                  Y: {Number(sensor.position_y).toFixed(2)} mm
                                </Typography>
                              )}
                              {sensor.position_z !== null && (
                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                  Z: {Number(sensor.position_z).toFixed(2)} mm
                                </Typography>
                              )}
                            </Box>
                            <Tooltip title="Visualizar posición 3D">
                              <IconButton
                                size="small"
                                onClick={() => onOpen3DDialog && onOpen3DDialog(sensor)}
                                sx={{ p: 0.5 }}
                              >
                                <VisibilityIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No configuradas
                          </Typography>
                        )
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {tipo === 'fibra_optica' && (
                    <TableCell>
                      {sensor.fos_sensor && getTipologiaFromProductType(sensor.fos_sensor.product_type) === 'Long-Gauge' && (
                        <Tooltip title="Configurar longitud de anclaje y coordenadas">
                          <IconButton
                            size="small"
                            onClick={() => onOpenLFalDialog && onOpenLFalDialog(sensor)}
                            sx={{ p: 0.5 }}
                          >
                            <SettingsIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    {hasDetails && (
                      <Tooltip title="Ver detalles">
                        <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
                {/* Sub-filas jerárquicas: Variables (FOS) o Desplazamiento (LVDT) */}
                {isExpanded && tieneSubFilas && (
                  <>
                    {/* Variables para FOS */}
                    {tieneVariables && sensor.variables && sensor.variables.map((variable) => {
                      return (
                        <TableRow
                          key={variable.id}
                          sx={{
                            bgcolor: alpha(tipoColor, 0.02),
                            '&:hover': {
                              bgcolor: alpha(tipoColor, 0.05),
                            },
                          }}
                        >
                          <TableCell sx={{ p: 0.5, pl: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box
                                sx={{
                                  width: 2,
                                  height: 20,
                                  bgcolor: tipoColor,
                                  borderRadius: 1,
                                }}
                              />
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Variable:
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, pl: 2 }}>
                              {variable.nombre_completo || variable.nombre_variable}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={variable.nombre_variable}
                              size="small"
                              sx={{
                                bgcolor: alpha(tipoColor, 0.2),
                                color: tipoColor,
                                height: 20,
                                fontSize: '0.65rem',
                                fontWeight: 500,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {variable.unidades ? (
                              <Chip
                                label={variable.unidades}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {variable.indice_columna_excel ? (
                              <Chip
                                label={variable.indice_columna_excel}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {variable.nombre_completo}
                            </Typography>
                          </TableCell>
                          {tipo === 'lvdt' && <TableCell />}
                          {tipo === 'lvdt' && <TableCell />}
                          {tipo === 'lvdt' && <TableCell />}
                          {tipo === 'fibra_optica' && <TableCell />}
                          {tipo === 'fibra_optica' && <TableCell />}
                          {tipo === 'fibra_optica' && <TableCell />}
                          <TableCell />
                        </TableRow>
                      );
                    })}
                    
                    {/* Desplazamiento para LVDT */}
                    {esLVDTConDesplazamiento && (
                      <TableRow
                        sx={{
                          bgcolor: alpha(tipoColor, 0.02),
                          '&:hover': {
                            bgcolor: alpha(tipoColor, 0.05),
                          },
                        }}
                      >
                        <TableCell sx={{ p: 0.5, pl: 4 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box
                              sx={{
                                width: 2,
                                height: 20,
                                bgcolor: tipoColor,
                                borderRadius: 1,
                              }}
                            />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Variable:
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, pl: 2 }}>
                            {sensor.nombre} - Desplazamiento
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label="Desplazamiento"
                            size="small"
                            sx={{
                              bgcolor: alpha(tipoColor, 0.2),
                              color: tipoColor,
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label="mm"
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            Calculado
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label="Calculados"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                              color: theme.palette.success.main,
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell />
                        <TableCell />
                      </TableRow>
                    )}
                  </>
                )}
                
                {/* Fila expandible con detalles (metadatos) */}
                {hasDetails && (
                  <TableRow>
                    <TableCell colSpan={tipo === 'lvdt' ? 10 : tipo === 'fibra_optica' ? 9 : 7} sx={{ py: 0, border: 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: alpha(tipoColor, 0.03) }}>
                          <Stack spacing={1.5}>
                            {sensor.descripcion && (
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Descripción:
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  {sensor.descripcion}
                                </Typography>
                              </Box>
                            )}
                            {Object.keys(metadatos).length > 0 && (
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>
                                  Metadatos adicionales:
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: 1,
                                  }}
                                >
                                  {Object.entries(metadatos).map(([key, value]) => (
                                    <Box
                                      key={key}
                                      sx={{
                                        p: 1,
                                        bgcolor: 'background.paper',
                                        borderRadius: 1,
                                        border: `1px solid ${alpha(tipoColor, 0.2)}`,
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block' }}>
                                        {key}:
                                      </Typography>
                                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                                        {String(value)}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Stack>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
